import React, { type ReactElement, useMemo, useRef, useState } from 'react';
import { Logger } from '@saviynt/common';
import {
  Box,
  Link as DSLink,
  Loader,
  StackingAlertBanner,
} from '@saviynt/design-system';

import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../shared/hooks';
import { type RootState } from '../../../../shared/types';
import { downloadBlobAsFile } from '../../../../shared/utils/utils';
import {
  useAcknowledgeExportMutation,
  useLazyDownloadExportQuery,
} from '../../data-layer/entReportsApi';
import useExportReport from '../../hooks/use-entExportReport';
import formatEntReportType from '../../types/entReportTypes';

const acknowledgedExportIds = new Set<string>();
const dismissedErrorBanners = new Set<string>();
const dismissedSuccessBanners = new Set<string>();

interface ExportBannerProps {
  className?: string;
}

interface DownloadLinkProps {
  exportId: string;
  isDownloading: boolean;
  onDownload: (id: string) => Promise<void>;
}

/**
 * DownloadLink - Reusable download link component with loading state
 */
function DownloadLink({
  exportId,
  isDownloading,
  onDownload,
}: DownloadLinkProps): ReactElement {
  const l = useInvertedLocalize();

  if (isDownloading) {
    return (
      <Box className='Sod-DownloadLink-loading'>
        <Loader kind='loop' size='small' format='inline' color='inverse' />
      </Box>
    );
  }

  return (
    <DSLink
      text={l('Download')}
      kind='button'
      onClick={() => onDownload(exportId)}
      color='neutral'
      suffixIconKind='Download'
      dataTheme='dark'
    />
  );
}

/**
 * ExportBanner - Reusable banner component for export notifications
 *
 * Advanced banner system that manages export status notifications with intelligent
 * duplicate prevention, download functionality, and acknowledgment tracking. Handles
 * multiple banner types including progress, success, and failure notifications.
 *
 * @component
 * @returns {React.ReactElement | null} Banner component with features including:
 *   - Multiple banner types (Information, Success, Critical) with auto-dismiss
 *   - Intelligent duplicate prevention for error and success banners
 *   - Download functionality with loading states and progress tracking
 *   - Automatic acknowledgment of failed exports when banners are dismissed
 *   - Report type filtering to prevent cross-contamination
 *   - Stacking banner support for multiple simultaneous notifications
 *
 * @example
 * <ExportBanner className='ReportTable-exportBanner' />
 */
function ExportBanner({ className }: ExportBannerProps): ReactElement | null {
  const l = useInvertedLocalize();
  const { closeBanner } = useExportReport();
  const [downloadExport] = useLazyDownloadExportQuery();
  const [ackExport] = useAcknowledgeExportMutation();
  const bannerState = useTypedSelector(
    (state) => state.aag.entReports.exportBanner
  );
  const {
    exportProgress,
    exportIdsByReportType,
    exportStatusByReportType = {},
    completedExportsCount,
    lastStatusReportTypes = [],
  } = useTypedSelector((state: RootState) => state.aag.entReports);

  const completedReportTypes = useMemo(() => {
    // Filter to only ENT report types
    const entReportTypes = [
      'INHERENT_ENTITLEMENT_RISK',
      'INHERENT_ENTITLEMENT_USER',
    ];

    const byStatus = Object.keys(exportStatusByReportType).filter(
      (rt) =>
        exportStatusByReportType[rt] === 'COMPLETED' &&
        entReportTypes.includes(rt)
    );

    if (byStatus.length > 0) {
      return byStatus;
    }

    return Object.keys(exportProgress).filter(
      (rt) =>
        typeof exportProgress[rt] === 'number' &&
        exportProgress[rt] >= 100 &&
        entReportTypes.includes(rt)
    );
  }, [exportStatusByReportType, exportProgress]);

  // Track locally dismissed completed banners by exportId so new runs (new ids)
  // for the same report type can show again
  const [dismissedCompletedIds, setDismissedCompletedIds] = useState<
    Set<string>
  >(new Set());

  // Track download loading state for each export ID
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  // Track last shown banner to prevent duplicates
  const lastShownBanner = useRef<string>('');

  const visibleCompletedItems = useMemo(() => {
    // If neither API count nor derived completed types indicate completion, nothing to show
    if (
      (!completedExportsCount || completedExportsCount <= 0) &&
      completedReportTypes.length === 0
    ) {
      return [] as { reportType: string; exportId?: string }[];
    }

    const uniqueItems = new Map<
      string,
      { reportType: string; exportId?: string }
    >();

    const items = completedReportTypes
      .map((reportType) => ({
        reportType,
        exportId: exportIdsByReportType[reportType],
      }))
      .filter(({ exportId }) => {
        // If we have an id and it was dismissed locally or globally, exclude it
        if (
          typeof exportId === 'string' &&
          (dismissedCompletedIds.has(exportId) ||
            dismissedSuccessBanners.has(exportId))
        ) {
          return false;
        }

        return true;
      });

    // Gate by report types present in the latest status payload only when provided
    const allowedReportTypes: string[] = Array.isArray(lastStatusReportTypes)
      ? lastStatusReportTypes.filter(
          (rt): rt is string => typeof rt === 'string'
        )
      : [];
    const gated =
      allowedReportTypes.length > 0
        ? items.filter((it) => allowedReportTypes.includes(it.reportType))
        : items;

    // Use Map to ensure unique items by exportId to prevent duplicates
    gated.forEach((item) => {
      const key = item.exportId || item.reportType;

      uniqueItems.set(key, item);
    });

    return Array.from(uniqueItems.values());
  }, [
    completedReportTypes,
    exportIdsByReportType,
    dismissedCompletedIds,
    completedExportsCount,
    lastStatusReportTypes,
  ]);

  // Create a safe key for the banner to force re-rendering
  const bannerKey = useMemo(
    () =>
      typeof bannerState.key === 'string' && bannerState.key.length > 0
        ? bannerState.key
        : `banner-${bannerState.title}-${Date.now()}`,
    [bannerState.key, bannerState.title]
  );

  return (
    <>
      {(() => {
        const bannerKeys = `${bannerState.title}-${bannerState.description}-${bannerState.colorTheme}`;
        const isDuplicate = lastShownBanner.current === bannerKeys;
        const shouldShow = bannerState.isVisible && !isDuplicate;

        if (bannerState.isVisible && isDuplicate) {
          closeBanner();

          return false;
        }

        if (shouldShow) {
          lastShownBanner.current = bannerKeys;
        }

        return shouldShow;
      })() ? (
        <StackingAlertBanner
          kind={bannerState.colorTheme === 'Information' ? 'low' : 'high'}
          key={bannerKey} // Force re-render with unique key
          colorTheme={bannerState.colorTheme}
          title={bannerState.title ? l(bannerState.title) : bannerState.title}
          description={bannerState.description}
          isVisible
          onCancel={() => {
            const isFailure = bannerState.title === 'Export Failed';
            const { exportId } = bannerState as { exportId?: string | null };

            // Track dismissed error banner
            dismissedErrorBanners.add(exportId ?? bannerState.title);

            // Clear last shown banner tracking
            lastShownBanner.current = '';

            closeBanner();

            if (isFailure && exportId && !acknowledgedExportIds.has(exportId)) {
              ackExport(exportId)
                .unwrap()
                .then(() => {
                  acknowledgedExportIds.add(exportId);
                })
                .catch((e: unknown) => {
                  Logger.log('error', 'Acknowledge failed:', e);
                });
            }
          }}
          className={className}
          shouldAutoDismiss
          autoDismissDelay={5000}
        />
      ) : null}

      {visibleCompletedItems.map(({ reportType, exportId }) => (
        <StackingAlertBanner
          className={className}
          key={`completed-${exportId ?? reportType}`}
          colorTheme='Success'
          title={l('Export Completed')}
          description={`${formatEntReportType(reportType)} ${l(
            'export has completed successfully.'
          )}`}
          isVisible
          shouldAutoDismiss
          autoDismissDelay={3000}
          onCancel={() => {
            const id = exportId ?? exportIdsByReportType[reportType];

            if (!id) {
              return;
            }

            // Add to both local and persistent tracking
            dismissedSuccessBanners.add(id);
            setDismissedCompletedIds((prev) => {
              const next = new Set(prev);

              next.add(id);

              return next;
            });
          }}
          LinkComponent={
            exportId || exportIdsByReportType[reportType] ? (
              <DownloadLink
                exportId={exportId ?? exportIdsByReportType[reportType]}
                isDownloading={downloadingIds.has(
                  exportId ?? exportIdsByReportType[reportType]
                )}
                onDownload={async (id: string) => {
                  try {
                    // Set loading state
                    setDownloadingIds((prev) => new Set([...prev, id]));

                    const file = await downloadExport(id).unwrap();

                    const suggestedFileName = (
                      file as unknown as { fileName?: string }
                    ).fileName;
                    const finalFileName = suggestedFileName ?? `${id}.xlsx`;

                    downloadBlobAsFile(file, finalFileName);
                  } catch (e) {
                    Logger.log('error', 'Download failed:', e);
                  } finally {
                    // Remove loading state
                    setDownloadingIds((prev) => {
                      const next = new Set(prev);

                      next.delete(id);

                      return next;
                    });
                  }
                }}
              />
            ) : null
          }
        />
      ))}
    </>
  );
}

ExportBanner.defaultProps = {
  className: '',
};

export default ExportBanner;
